/**
 * Private Company 409A Calculator
 *
 * Default calculator for most private company clients.
 * Calculates in-the-money value: shares * (sharePrice - strikePrice)
 *
 * This is the standard valuation method used by most clients (lines 6032-6035 in index.js):
 *   latestValue[key] = Number(userDataShares[key].owned) * (Number(latest[0].sharePrice) - strikePrice);
 *
 * Supports optional floor at zero for underwater positions (isSEQ behavior).
 * Outputs both total value (all owned) and vested value for display flexibility.
 */

import { basisForBreakdown } from '../helpers/equity-calculation-utils.js';
import { attachCAGR } from '../helpers/cagr-utils.js';

const PrivateCompany409ACalculator = {
	methodId: 'private_company_409a',
	displayName: 'Private Company (In-the-Money Value)',
	requiredDataSources: ['valuations', 'strikePrice'],

	// Graph configuration - stacked area chart shows value by share class over time
	graphConfig: {
		recommendedType: 'time_series_stacked',
		timeSeriesStrategy: 'valuation_by_class',
		supportsTimeSeries: true,
	},

	// CAGR semantic: end-value = classData.value (no transfer adjustment).
	// 409A clients don't have a transfers concept in the headline math.
	cagrOptions: { excludeTransfers: false },

	/**
	 * Calculate in-the-money value
	 * @param {Object} input
	 * @param {Object} input.holdings - { owned, vested, totalTarget, sharePrice, priorSharePrice, strikePrice } by class
	 * @param {string[]} input.shareClasses - Array of share class keys
	 * @param {Object} input.options - { floorAtZero: boolean }
	 * @returns {Object} CalculationResult
	 */
	calculate: function (input) {
		const holdings = input.holdings;
		const shareClasses = input.shareClasses || Object.keys(holdings.owned || {});
		const options = input.options || {};
		const floorAtZero = options.floorAtZero || false;

		let totalValue = 0; // Value based on all owned shares
		let vestedValue = 0; // Value based on vested shares only
		let unvestedValue = 0; // Value of unvested grants (using totalTarget if available)
		const breakdown = [];

		shareClasses.forEach(function (key) {
			const owned = holdings.owned[key] || 0;
			const vested = holdings.vested?.[key] !== undefined ? holdings.vested[key] : owned; // Default to owned only if vesting data is missing
			// #6266: unvested must use the uncapped schedule-vested count, not `vested`
			// (which is capped at owned) — otherwise a redemption that drops owned back
			// to 0 makes already-vested shares look unvested again.
			const scheduleVested = holdings.scheduleVested?.[key] !== undefined ? holdings.scheduleVested[key] : vested;
			const totalTarget = holdings.totalTarget?.[key] || 0; // Total target including unvested grants
			const sharePrice = holdings.sharePrice[key] || 0;
			const priorSharePrice = holdings.priorSharePrice?.[key] || 0;
			const strikePrice = holdings.strikePrice[key] || 0;

			// Calculate per-share value (price - strike)
			const perShareValue = sharePrice - strikePrice;

			// Calculate price change percentage from prior valuation
			// priceChange is signed (for direction conditionals), absPriceChange is for display
			let priceChange = null;
			let absPriceChange = null;
			if (priorSharePrice > 0) {
				priceChange = Math.round(((sharePrice - priorSharePrice) / priorSharePrice) * 100);
				absPriceChange = Math.abs(priceChange);
			}

			// In-the-money value: qty * (price - strike)
			// This matches line 6034: Number(userDataShares[key].owned) * (Number(latest[0].sharePrice) - strikePrice)
			let value = owned * perShareValue;
			let classVestedValue = vested * perShareValue;

			// Optional: floor at zero for underwater positions (isSEQ behavior from line 6360)
			if (floorAtZero) {
				value = Math.max(0, value);
				classVestedValue = Math.max(0, classVestedValue);
			}

			// Calculate unvested value:
			// If totalTarget is available (BW-style vesting), use (totalTarget - vested) * price
			// Otherwise fall back to (owned - vested) * price
			let classUnvestedValue;
			if (totalTarget > 0) {
				// BW-style: unvested = target shares that haven't vested yet
				const unvestedShares = totalTarget - scheduleVested;
				classUnvestedValue = unvestedShares * perShareValue;
				if (floorAtZero) {
					classUnvestedValue = Math.max(0, classUnvestedValue);
				}
			} else {
				// Standard: unvested = owned - vested
				classUnvestedValue = value - classVestedValue;
			}

			totalValue += value;
			vestedValue += classVestedValue;
			unvestedValue += classUnvestedValue;

			breakdown.push({
				classKey: key,
				className: key,
				value: value,
				vestedValue: classVestedValue,
				unvestedValue: classUnvestedValue,
				owned: owned,
				vested: vested,
				scheduleVested: scheduleVested,
				totalTarget: totalTarget,
				sharePrice: sharePrice,
				priorSharePrice: priorSharePrice,
				priceChange: priceChange,
				absPriceChange: absPriceChange,
				strikePrice: strikePrice,
				basis: basisForBreakdown(holdings, key),
				contributions: Number(holdings.contributions?.[key]) || 0,
			});
		});

		// Aggregate paid-in capital across classes. Skips the -1 sentinel via
		// basisForBreakdown, plus any class the input-builder flagged as excluded
		// (e.g. Urban Alpha sold classes). Per-class breakdown.basis stays
		// populated for all classes so equity cards still surface it.
		const excludeFromPaidIn = holdings.excludeFromPaidInCapital || {};
		const totalBasis = breakdown.reduce((sum, b) => {
			if (excludeFromPaidIn[b.classKey]) return sum;
			return sum + (typeof b.basis === 'number' ? b.basis : 0);
		}, 0);

		const totalPaidInCapital = totalBasis;
		const netValue = totalValue - totalPaidInCapital;

		const result = {
			totalValue: totalValue,
			vestedValue: vestedValue,
			unvestedValue: unvestedValue,
			totalBasis: totalBasis,
			totalPaidInCapital: totalPaidInCapital,
			netValue: netValue,
			breakdown: breakdown,
			fundMetrics: null,
			metadata: {
				methodUsed: this.methodId,
				calculationDate: new Date(),
				options: { floorAtZero: floorAtZero },
			},
		};
		attachCAGR(result, holdings.cagrAnchors, { ...this.cagrOptions, ...holdings.cagrOptions });
		return result;
	},
};

export { PrivateCompany409ACalculator };
